import { memo, useMemo, type FC } from 'react';
import ReactMarkdown from 'react-markdown';
import styled from '@emotion/styled';
import { TextMessageBubble } from 'components/Dialogue';
import { MessageExtendComplex } from '../MessageExtend/MessageExtendComplex';
import { getMetaFromSender } from '../../utils';
import { BaseMsgProps } from './types';

const FINAL_RESPONSE_EVENT = 'final-response';
const Markdown = ReactMarkdown as unknown as FC<{ children: string }>;

export const getJolectAiGuideMarkdown = (content?: Conversation.MsgJlAiGuide) => {
  const events = content?.events;
  if (!events?.length) {
    return undefined;
  }

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event.event !== FINAL_RESPONSE_EVENT) {
      continue;
    }

    try {
      const data = JSON.parse(event.data) as { message?: unknown };
      if (typeof data.message === 'string') {
        return data.message;
      }
    } catch {
      continue;
    }
  }

  return undefined;
};

export const JolectAiGuideMsg = memo((props: BaseMsgProps) => {
  const { msg } = props;
  const markdown = useMemo(
    () => getJolectAiGuideMarkdown(msg.raw.jlAiGuideContent),
    [msg.raw.jlAiGuideContent],
  );

  if (!markdown) {
    return null;
  }

  const { layout, color } = getMetaFromSender(msg.sender);

  return (
    <MessageExtendComplex
      {...props}
      showSourceContent={false}
      content={
        <BubbleAlign layout={layout}>
          <StyledTextMessageBubble color={color} className={layout} layout={layout}>
            <Markdown>{markdown}</Markdown>
          </StyledTextMessageBubble>
        </BubbleAlign>
      }
    />
  );
});

const BubbleAlign = styled.div<{ layout: 'left' | 'right' }>`
  display: flex;
  justify-content: ${({ layout }) => (layout === 'right' ? 'flex-end' : 'flex-start')};
  width: 100%;
`;

const StyledTextMessageBubble = styled(TextMessageBubble)<{ layout: 'left' | 'right' }>`
  max-width: min(520px, 100%);

  .origin-text {
    white-space: normal;
  }

  .origin-text > *:first-child {
    margin-top: 0;
  }

  .origin-text > *:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 8px;
  }

  ul,
  ol {
    margin: 0 0 8px 18px;
    padding: 0;
  }

  li + li {
    margin-top: 4px;
  }

  code {
    padding: 1px 4px;
    border-radius: 3px;
    background: var(--fett-bg-light);
    font-size: 12px;
    white-space: break-spaces;
  }

  pre {
    max-width: 100%;
    margin: 0 0 8px;
    padding: 8px;
    overflow-x: auto;
    border-radius: 4px;
    background: var(--fett-bg-light);

    code {
      padding: 0;
      background: transparent;
    }
  }

  blockquote {
    margin: 0 0 8px;
    padding-left: 8px;
    border-left: 3px solid var(--fett-border-default);
    color: var(--fett-text-tertiary);
  }

  a {
    color: var(--fett-app-primary-color);
    word-break: break-all;
  }

  table {
    max-width: 100%;
    margin: 0 0 8px;
    border-collapse: collapse;
    display: block;
    overflow-x: auto;
  }

  th,
  td {
    padding: 4px 6px;
    border: 1px solid var(--fett-border-default);
    text-align: left;
  }
`;
